import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import * as R from 'ramda'
import {
  Button,
  ChartIcon,
  EntitiesList,
  GaugeIcon,
  IconButton,
  McatForChartIcon
} from 'examkrackers-components'
import { Exam, Course } from 'types'
import {
  getAccountType,
  getIsImpersonate,
  getStudent
} from 'modules/auth/ducks/selectors'
import SelectTimeOption from 'modules/exams/components/SelectTimeOption'
import ModalChangeAccessPeriod from 'modules/exams/components/ModalChangeAccessPeriod'
import ModalExamLogs from 'modules/exams/components/ModalExamLogs'
import ExamButton from 'modules/exams/components/ExamButton'
import { getNavigation } from 'modules/student/ducks/selectors'
import { getCurrentCourse } from 'modules/courses/ducks/selectors'

import { SORT_DIRECTION } from 'utils/table'
import {
  EXAM_STATUS,
  EXAM_TIME_OPTION_VALUES,
  SCORE_CALCULATION_METHOD
} from 'utils/exam'
import { DATE_FORMATS, formatDate, isDateBeforeToday } from 'utils/date'
import { COURSE_TYPES } from 'utils/courses'

import { isNotNilOrEmpty, mapIndexed } from 'utils/ramda'
import { isEmpty, pathOr } from 'ramda'
import AnalyticsService from '../../../services/AnalyticsService'
import { ANALYTICS_EVENTS } from '../../../utils/analytics'
import { getFreeTrialExams } from 'services/ExamsService'
import RetakeOverallModal from 'modules/exams/components/RetakeOverallModal'
import { selectBooksFlaggedAsFreeTrial } from 'modules/freeTrial/ducks/selectors'
import { isExamIncludedInFreeTrial } from 'modules/freeTrial/utils'

export const FreeTrialExams = (): JSX.Element => {
  const [refresh, setRefresh] = useState(false)
  const { t } = useTranslation()
  const studentDetails = useSelector(getStudent)
  const accountType: string = useSelector(getAccountType)
  const navigation = useSelector(getNavigation)
  const { push } = useHistory()
  const [examsList, setExamsList] = useState<Exam[]>([])

  const handleRefresh = () => setRefresh(true)

  // Rerender on refresh
  useEffect(() => {
    if (refresh) {
      setRefresh(false)
    }
  }, [refresh])

  const isImpersonate = useSelector(getIsImpersonate)
  const currentCourse: Course = useSelector(getCurrentCourse)
  const isFreeTrialCourse = R.pipe(
    R.propOr('', 'type'),
    R.equals(COURSE_TYPES.freeTrial)
  )(currentCourse)

  const booksFlaggedAsFreeTrial: BookIsFreeTrialFlags[] = useSelector(
    selectBooksFlaggedAsFreeTrial
  )

  const fetchFreeTrialExams = () => {
    const handleSuccess = response => {
      setExamsList(
        [...response.data].sort((a, b) => a.title.localeCompare(b.title))
      )
    }
    const handleError = error => {
      console.error(error)
    }

    getFreeTrialExams().then(handleSuccess).catch(handleError)
  }

  useEffect(() => {
    fetchFreeTrialExams()
  }, [])

  const findFirstSectionIdByExamId = (id: string) =>
    R.pipe(
      R.find(R.propEq('exam_id', id)),
      R.propOr([], 'sections'),
      R.head,
      R.propOr('', 'id')
    )(navigation)

  const getCalculatedMinutes = (minutes: number, id: string) => {
    const timeOptionElement = document.querySelectorAll(
      `[data-exam-time-option-id="${id}"]`
    )[0]
    const selectedTimeOption =
      timeOptionElement?.getAttribute('data-exam-time-option-value') || '1'
    return `${minutes * Number(selectedTimeOption)} ${t('exams.table.minutes')}`
  }

  const redirectToAnswerSheet =
    (id: string, sectionId: string | null, exam: Exam) => () => {
      const getPercentileRank = R.pipe(
        R.propOr('{}', 'scores'),
        JSON.parse,
        R.pathOr(0, ['exam', 'percentile_rank'])
      )(exam)

      const getRawScore = R.pipe(
        R.propOr('{}', 'scores'),
        JSON.parse,
        R.pathOr(0, ['exam', 'amount_correct'])
      )(exam)

      const getScaledScore = R.pipe(
        R.propOr('{}', 'scores'),
        JSON.parse,
        R.pathOr(0, ['exam', 'scaled_score'])
      )(exam)

      AnalyticsService(studentDetails).logEvent(
        ANALYTICS_EVENTS.answerSheetReviewed,
        {
          'Exam Title': R.propOr('', 'exam_title', exam),
          'Exam format': R.propOr('', 'type_title', exam),
          'Account type': accountType,
          'Precentile Rank': getPercentileRank,
          'Raw Score': getRawScore,
          'Scaled Score': getScaledScore,
          'Section Title': R.propOr('', 'title', exam),
          'Section Number': R.propOr('', 'section_order', exam)
        }
      )

      push(
        `/exam/${id}/score-report/answers-graph/${
          sectionId || findFirstSectionIdByExamId(id)
        }/answer-sheet`
      )
    }

  const redirectToDiagnostics =
    (id: string, sectionId: string | null, exam: Exam) => () => {
      const getPercentileRank = R.pipe(
        R.propOr('{}', 'scores'),
        JSON.parse,
        R.pathOr(0, ['exam', 'percentile_rank'])
      )(exam)

      const getRawScore = R.pipe(
        R.propOr('{}', 'scores'),
        JSON.parse,
        R.pathOr(0, ['exam', 'amount_correct'])
      )(exam)

      const getScaledScore = R.pipe(
        R.propOr('{}', 'scores'),
        JSON.parse,
        R.pathOr(0, ['exam', 'scaled_score'])
      )(exam)

      AnalyticsService(studentDetails).logEvent(
        ANALYTICS_EVENTS.diagnosticsReviewed,
        {
          'Exam Title': R.propOr('', 'exam_title', exam),
          'Exam format': R.propOr('', 'type_title', exam),
          'Account type': accountType,
          'Precentile Rank': getPercentileRank,
          'Raw Score': getRawScore,
          'Scaled Score': getScaledScore,
          'Section Title': R.propOr('', 'title', exam),
          'Section Number': R.propOr('', 'section_order', exam)
        }
      )

      push(
        `/exam/${id}/score-report/answers-graph/${
          sectionId || findFirstSectionIdByExamId(id)
        }/diagnostic`
      )
    }

  const redirectToScoreSheet = (id: string) => () => {
    push(`/exam/${id}/score-report/score-sheet`)
  }

  const getExamScore = (exam, scoreType) => {
    const scores = JSON.parse(R.propOr('{}', 'scores', exam))
    const examScores = R.propOr({}, 'exam', scores)
    return R.propOr('-', scoreType, examScores)
  }

  const getSectionsAsTableRows = row => {
    const sectionCount = R.pathOr(1, [
      'exam_length',
      'summary',
      'sectionCount'
    ])(row)

    if (sectionCount === 1) return []
    const sections: any[] = R.pathOr([], ['exam_length', 'sections'], row)
    if (isEmpty(sections)) {
      return []
    } else {
      const scores: any = JSON.parse(R.propOr('{}', 'scores', row))
      return sections.map((section, index) => {
        const sectionScores = isNotNilOrEmpty(scores)
          ? scores.sections.find(sec => sec.title === section.section)
          : {}
        return {
          ...R.pick(
            [
              'status',
              'time_option',
              'accessible_to',
              'completed_at',
              'id',
              'exam_id',
              'type_title'
            ],
            row
          ),
          sectionId: R.propOr(null, 'id', sectionScores),
          title: section.section,
          section_order: index + 1,
          exam_title: row.title,
          score_calculation_method: row.score_calculation_method,
          exam_length: {
            summary: {
              minutes: section.sectionMinutes,
              questions: section.amount
            }
          },
          scores: JSON.stringify({
            exam: {
              percentile_rank: R.propOr(null, 'percentile_rank', sectionScores),
              amount_correct: R.propOr(null, 'amount_correct', sectionScores),
              scaled_score: R.propOr(null, 'scaled_score', sectionScores)
            }
          })
        }
      })
    }
  }

  const scoreCalculationMethod = exam =>
    pathOr('', ['score_calculation_method'], exam)

  const hideScaledScore = exam =>
    scoreCalculationMethod(exam) === SCORE_CALCULATION_METHOD.percentile

  const getExamStatus = exam => R.pathOr(EXAM_STATUS.scheduled, ['status'], exam)
  const isFreeTrialAsset = exam =>
    isExamIncludedInFreeTrial(exam, booksFlaggedAsFreeTrial)

  const getExamStatusLabel = exam => {
    switch (true) {
      case isFreeTrialCourse &&
        !isExamIncludedInFreeTrial(exam, booksFlaggedAsFreeTrial):
        return '-'
      case isFreeTrialCourse &&
        exam.status === EXAM_STATUS.scheduled &&
        isExamIncludedInFreeTrial(exam, booksFlaggedAsFreeTrial):
        return 'Available'
      case exam.status !== EXAM_STATUS.completed:
        return t(`examStatus.${getExamStatus(exam)}`)
      case exam.status === EXAM_STATUS.completed:
        return (
          <div>
            {t('examStatus.completed')}
            <ExamCompletionDate>
              {formatDate(
                R.propOr('', 'completed_at', exam),
                DATE_FORMATS.dashed
              )}
            </ExamCompletionDate>
          </div>
        )
      default:
        return t(`examStatus.${getExamStatus(exam)}`)
    }
  }

  const getRows = (source, isLvl2) => {
    return R.pipe(
      // @ts-ignore
      R.reject(R.propEq('status', EXAM_STATUS.unavailable)),
      // @ts-ignore
      mapIndexed((exam: Exam, index) => {
        const completionDone: number = R.propOr(0, 'completions_done', exam)
        const maxCompletions: number = R.propOr(0, 'max_completions', exam)
        const remainAttempts = maxCompletions - completionDone

        return {
          id: `row-exam-${exam.id}-${exam.exam_id}-${
            isLvl2 ? `row-${index}` : 'exam'
          }`,
          children: getRows(getSectionsAsTableRows(exam), true),
          cells: [
            {
              columnId: 'type',
              children: (
                <TypeContainer>{R.propOr('', 'title', exam)}</TypeContainer>
              )
            },
            {
              children: (
                <>
                  {getCalculatedMinutes(
                    R.pathOr(0, ['exam_length', 'summary', 'minutes'], exam),
                    exam.id
                  )}
                </>
              ),
              columnId: 'duration',
              cellProps: { align: 'left' }
            },
            {
              children: (
                <TimeOptionContainer>
                  <SelectTimeOption
                    onChange={handleRefresh}
                    disabled={isLvl2}
                    timeOption={R.pathOr(
                      EXAM_TIME_OPTION_VALUES.regular,
                      ['time_option'],
                      exam
                    )}
                    examStatus={getExamStatus(exam)}
                    examId={exam.id}
                  />
                </TimeOptionContainer>
              ),
              columnId: 'time_option',
              cellProps: { align: 'left' }
            },
            {
              children: getExamStatusLabel(exam),
              columnId: 'status',
              cellProps: { align: 'left' }
            },
            {
              children: (
                <AccessContainer>
                  <RemainingAttemptsWrapper
                    id={`${exam.id}-remaining-attempts`}
                    className='remaining-attempts'
                  >
                    {remainAttempts}
                  </RemainingAttemptsWrapper>
                </AccessContainer>
              ),
              columnId: 'remaining_attempts',
              cellProps: { align: 'left' }
            },
            {
              children: (
                <AccessContainer>
                  <RemainingAttemptsWrapper
                    id={`${exam.id}-remaining-attempts`}
                    className='remaining-attempts'
                  >
                    {completionDone > 0 && <RetakeOverallModal exam={exam} />}
                  </RemainingAttemptsWrapper>
                </AccessContainer>
              ),
              columnId: 'retake_overall',
              cellProps: { align: 'left' }
            },
            {
              children: (
                <>
                  {
                    // @ts-ignore
                    getExamScore(exam, 'percentile_rank') === '-'
                      ? '-'
                      : // @ts-ignore
                        `${getExamScore(exam, 'percentile_rank')}%`
                  }
                </>
              ),
              columnId: 'rank',
              cellProps: { align: 'left' }
            },
            {
              children: (
                <div>
                  {
                    // @ts-ignore
                    getExamScore(exam, 'amount_correct') === '-'
                      ? '-'
                      : `${getExamScore(exam, 'amount_correct')} of ${R.pathOr(
                          '-',
                          ['exam_length', 'summary', 'questions'],
                          exam
                        )}`
                  }
                </div>
              ),
              columnId: 'rawScore',
              cellProps: { align: 'left' }
            },
            {
              children: (
                <div>
                  {hideScaledScore(exam)
                    ? '-'
                    : getExamScore(exam, 'scaled_score')}
                </div>
              ),
              columnId: 'scaledScore',
              cellProps: { align: 'left', width: '5%' }
            },
            {
              children: (
                <ButtonsContainer>
                  {R.pathOr(1, ['exam_length', 'summary', 'sectionCount'])(
                    exam
                  ) === 1 ? (
                    <IconButtons>
                      {isLvl2 ? null : (
                        <IconButton
                          variant='filled'
                          color='orange'
                          icon={<GaugeIcon />}
                          onClick={redirectToScoreSheet(exam.id)}
                          tooltip={t('exams.table.tooltip.scoreSheet')}
                          tooltipId={`exams-table-score-sheet-tooltip-${exam.id}`}
                          disabled={exam.status !== EXAM_STATUS.completed}
                        />
                      )}
                      <IconButton
                        variant='filled'
                        color='orange'
                        // @ts-ignore
                        disabled={exam.status !== EXAM_STATUS.completed}
                        icon={<McatForChartIcon />}
                        tooltip={t('exams.table.tooltip.answers')}
                        tooltipId={`exams-table-answers-tooltip-${exam.id}`}
                        onClick={redirectToAnswerSheet(
                          exam.id,
                          R.propOr(null, 'sectionId', exam),
                          exam
                        )}
                      />
                      <IconButton
                        variant='filled'
                        color='orange'
                        // @ts-ignore
                        disabled={exam.status !== EXAM_STATUS.completed}
                        icon={<ChartIcon />}
                        onClick={redirectToDiagnostics(
                          exam.id,
                          R.propOr(null, 'sectionId', exam),
                          exam
                        )}
                        tooltip={t('exams.table.tooltip.diagnostics')}
                        tooltipId={`exams-table-diagnostics-tooltip-${exam.id}`}
                      />
                    </IconButtons>
                  ) : (
                    <AlignedIconButtons>
                      <IconButton
                        variant='filled'
                        color='orange'
                        icon={<GaugeIcon />}
                        onClick={redirectToScoreSheet(exam.id)}
                        disabled={exam.status !== EXAM_STATUS.completed}
                        tooltip={t('exams.table.tooltip.scoreSheet')}
                        tooltipId={`exams-table-score-sheet-tooltip-${exam.id}`}
                      />
                    </AlignedIconButtons>
                  )}
                  {isNotNilOrEmpty(R.propOr('', 'type_type', exam)) ? (
                    <ExamButton
                      status={R.propOr(EXAM_STATUS.scheduled, 'status', exam)}
                      id={R.propOr('', 'id', exam)}
                      exam={exam}
                      isImpersonated={R.propOr(
                        false,
                        'is_impersonated',
                        studentDetails
                      )}
                    />
                  ) : (
                    <FakeButton />
                  )}
                  {isImpersonate &&
                    (isLvl2 ? <FakeExamLogs /> : <ModalExamLogs exam={exam} />)}
                </ButtonsContainer>
              ),
              columnId: 'action',
              cellProps: { width: '19%' }
            }
          ]
        }
      })
      // @ts-ignore
    )(source)
  }

  const rows = useMemo(() => {
    return getRows(examsList, false)
  }, [examsList, booksFlaggedAsFreeTrial])

  const headers = [
    {
      columnId: 'title',
      sortable: false,
      id: 'title',
      children: t('exams.table.headers.title')
    },
    {
      columnId: 'duration',
      sortable: false,
      id: 'duration',
      children: t('exams.table.headers.time'),
      align: 'left'
    },
    {
      columnId: 'time_option',
      sortable: false,
      id: 'time_option',
      children: t('exams.table.headers.time_option'),
      align: 'left'
    },
    {
      columnId: 'status',
      sortable: false,
      id: 'status',
      children: t('exams.table.headers.status'),
      align: 'left'
    },
    {
      columnId: 'remaining_attempts',
      sortable: false,
      id: 'remaining_attempts',
      children: (
        <WrappedHeader>
          {t('exams.table.headers.remainingAttempts')}
        </WrappedHeader>
      ),
      align: 'left'
    },
    {
      columnId: 'past_scores',
      sortable: false,
      id: 'past_scores',
      children: t(''),
      align: 'left'
    },
    {
      columnId: 'rank',
      sortable: false,
      id: 'rank',
      children: t('exams.table.headers.rank'),
      align: 'left'
    },
    {
      columnId: 'rawScore',
      sortable: false,
      id: 'rawScore',
      children: t('exams.table.headers.rawScore'),
      align: 'left'
    },
    {
      columnId: 'scaledScore',
      sortable: false,
      id: 'scaledScore',
      children: t('exams.table.headers.scaledScore'),
      align: 'left'
    },
    {
      columnId: 'action',
      sortable: false,
      id: 'action',
      children: t('exams.table.headers.action'),
      align: 'left'
    }
  ]

  return (
    <TableContainer
      isImpersonate={isImpersonate as boolean}
      id='exams-list-container'
    >
      <EntitiesList
        paginationPage={1}
        size='m'
        resultsText=''
        headers={headers}
        // @ts-ignore
        rows={rows}
        emptyStateText={t('exams.table.emptyState')}
        onTableStateChange={() => {}}
        highlight
        defaultSortDirection={SORT_DIRECTION.asc}
        defaultPage={1}
      />
    </TableContainer>
  )
}

const TableContainer = styled.div<{ isImpersonate?: boolean }>`
  background: ${({ theme }) => theme.colors.backgrounds.main};
  box-shadow: ${props => props.theme.shadows.mainShadow};
  margin-bottom: 60px;

  & > div > div:nth-child(2) {
    background: transparent;
    box-shadow: none;
  }

  & > div > div:last-child {
    display: none;
  }

  table {
    thead {
      height: 40px;
    }

    button {
      min-width: 86px;
    }

    td {
      padding: 7px;
      line-height: 1;
    }

    th {
      position: sticky;
      top: ${props => props.theme.dimensions.studentTopNavHeight};
      z-index: ${({ theme }) => theme.zIndex.navigation - 2};
    }
  }

  .accessible-date {
    min-width: 85px;
  }
`

const TimeOptionContainer = styled.div`
  min-width: 120px;
`

const ButtonsContainer = styled.div`
  display: flex;
  align-items: left;
  justify-content: flex-end;
  gap: 12px;
`

const TypeContainer = styled.div`
  display: flex;
  align-items: left;
`

const AccessContainer = styled.div`
  display: flex;
  align-items: left;
  justify-content: left;
`

const ExamCompletionDate = styled.div`
  font-size: 11px !important;
  margin-top: 2px;
  color: ${({ theme }) => theme.colors.main.grey600};
`

const IconButtons = styled.div`
  display: flex;
  align-items: left;
  justify-content: flex-start;
  gap: 12px;

  button {
    min-width: unset !important;
  }
`

const AlignedIconButtons = styled.div`
  display: flex;
  align-items: left;
  margin-right: 88px;
  gap: 12px;

  button {
    min-width: unset !important;
  }
`

const FakeButton = styled(Button)`
  opacity: 0;
  cursor: default;
  pointer-events: none;
`

const FakeExamLogs = styled.div`
  display: inline-block;
  min-width: 69px;
`

const RemainingAttemptsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: left;
  justify-content: left;
`

const WrappedHeader = styled.div`
  max-width: 76px;
  word-wrap: break-word;
  white-space: break-spaces;
  text-align: left;
`

export default FreeTrialExams
